//  LA CUENTA DE LOS PROVEEDORES  (punto 9)
//
//  La base (0050, D113): la deuda nace con cada remito que trae costo, el pago
//  sale de una caja (`pagar_proveedor`), se anula con una fila espejo
//  (`anular_pago_proveedor`) y se corrige con un ajuste con motivo
//  (`ajustar_proveedor`). Esta pantalla deja hacer esas tres cosas a quien tiene
//  el permiso. No calcula ningún saldo: lo suma la base en `v_proveedores_cc`.
//
//  · La abre `proveedor.ver_cuenta` (D114), del rol `dueno`, y del admin global
//    sólo con la casilla de Ajustes prendida (D117, D164). Sin el permiso la vista contesta
//    vacío sin error: por eso la pestaña ni se muestra (arranque.ts).
//  · La cuenta no sabe todo (D106): una línea de remito SIN costo no generó
//    deuda. `recepciones_sin_costo` lo cuenta; un saldo en cero con remitos sin
//    costo NO es «no se le debe nada».
//  · El pago es un acto de plata (D93): se le pregunta a `mis_cajas()` de qué
//    caja sale y si se puede operar, ANTES de ofrecer el botón, que no vuelve si
//    la base no contestó (D91).
//  · Anular un pago exige estar habilitado en la caja de donde SALIÓ, que puede
//    no ser la de hoy (0052 agregó `caja_id` a la vista: el nombre de la caja lo
//    tapa `caja.ver`, que el dueño no tiene).

import { $, $$, E_MIS_CAJAS, MIS_CAJAS, aviso, avisoHtml, cajaDelActo, esc, fecha, limpiar, misCajas, muevePlata, noContesto, noHabilitado, nombreCaja, num, pedir, puede, puedoActo, puedoOperar, repintar, rpc, sb, trabado, trabarSiNoContesto, pedirTodo, ordenarPor } from './nucleo';
import { COTIZ, ERROR_COTIZ_COBRO, ERROR_MEDIOS, MEDIOS, MONEDAS, catalogosCobro,
         frenoMonedas } from './cobros';
import { abrirComoFicha, cerrarComoFicha } from './navegacion';

// ---------------------------------------------------------------------
//  El estado del módulo
// ---------------------------------------------------------------------
type Cuenta = {
  proveedor_id: number; nombre: string; cuit: string | null; activo: boolean | null;
  saldo: number; ultimo_movimiento: string | null; movimientos: number;
  recepciones_sin_costo: number | null;
};
let CUENTAS: Cuenta[] = [];
//  Tres estados antes de pintar (D103): sin contestar, no se pudo leer, leída.
//  `LEIDA` false con `ERROR_CUENTAS` vacío es el primero.
let LEIDA = false, ERROR_CUENTAS = '';
let ABIERTA: number | null = null;
let ERROR_MOVS = '';
//  La moneda de la cuenta, `costo.moneda`. `null` es «no se sabe» (la lectura
//  falló). Sin fila en `config` la base usa 'USD', y eso sí es un dato.
let MONEDA: string | null = null, ERROR_MONEDA = '';

//  Los dos contadores de viaje (regla del 27/9): uno para la lista y uno para la
//  cuenta abierta. Una respuesta vieja no pinta nada.
let SEQ_LISTA = 0, SEQ_CUENTA = 0;

//  Un movimiento de la cuenta abierta, tal como llega de la vista.
let MOVS: any[] = [];
//  Cuántos movimientos se piden de una (D122). El servidor corta en mil filas
//  y NO avisa; con un tope propio, más chico, el corte es nuestro y se dice.
//  El saldo del título no depende de esto: lo suma la base entero.
const TOPE_MOVS = 500;

//  El pago que se está por anular, con su motivo a medio escribir: vive acá y no
//  en el DOM, porque la tabla se repinta después de cada acto.
let ANULANDO: number | null = null;
//  Los pagos cuya anulación NO CONTESTÓ (D91): quizá quedaron anulados. Hasta
//  volver a entrar a la pestaña no se ofrece anularlos de nuevo (0052).
const ANULACION_INCIERTA = new Set<number>();
//  Los dos actos de plata en viaje: ningún repintado enciende su botón mientras
//  vuelan (un segundo clic es un segundo pago).
let PAGANDO = false, AJUSTANDO = false;
//  De qué cuenta es lo escrito en el formulario de pago. Un pago nuevo o una
//  cuenta distinta lo vuelve a sugerir; una relectura NO pisa lo escrito.
let PAGO_DE: number | null = null;
//  `proveedor.cargo_automatico` (0050): apagado, los remitos NO generan deuda y
//  «la deuda nace con el remito» es falso. `null` es «no se sabe».
let CARGO_AUTO: boolean | null = true;
//  Lista vacía sin error: ¿hay proveedores y no los puedo ver, o no hay ninguno?
//  (la casilla puede apagarse con la pestaña abierta: la vista contesta cero filas).
let HAY_PROVEEDORES: boolean | null = null;

// ---------------------------------------------------------------------
//  Lecturas
// ---------------------------------------------------------------------
const rotulo = () => MONEDA ?? '(no se sabe la moneda)';

//  Las dos claves de `config` que cambian lo que esta pantalla puede decir.
//  Sin la fila, lo que vale es lo que usa la base: 'USD' y `true`.
async function leerConfig(){
  const { data, error } = await pedir(sb.from('config').select('clave,valor')
    .in('clave', ['costo.moneda', 'proveedor.cargo_automatico']));
  ERROR_MONEDA = error ? error.message : '';
  const v = (k: string) => (data || []).find((x: any) => x.clave === k)?.valor;
  MONEDA = error ? null : String(v('costo.moneda') ?? 'USD');
  const ca = v('proveedor.cargo_automatico');
  CARGO_AUTO = error ? null : ca == null ? true : (ca === true || ca === 'true');
}

//  `entrando` es el camino de la pestaña (arranque.ts): ahí se olvida lo que
//  quedó incierto. Las relecturas que siguen a un acto pasan `false`.
export async function cargarProveedores(entrando = true){
  if (entrando) ANULACION_INCIERTA.clear();
  const mio = ++SEQ_LISTA;
  //  El estado CERO, sólo la primera vez: al releer, la tabla de antes sigue a
  //  la vista y sus botones los apaga la guarda del contador.
  if (!LEIDA && !ERROR_CUENTAS)
    $('#tblProveedoresCC').innerHTML =
      '<tr><td colspan="4" class="vacio">Leyendo las cuentas…</td></tr>';

  const [r] = await Promise.all([
    //  Entera: los proveedores pueden ser más de mil.
    pedirTodo(() => sb.from('v_proveedores_cc')
      .select('proveedor_id,nombre,cuit,activo,saldo,ultimo_movimiento,movimientos,recepciones_sin_costo'),
      { por: 'proveedor_id', orden: ordenarPor('nombre', 'proveedor_id') }),
    leerConfig(), catalogosCobro(), misCajas(),
  ]);
  if (mio !== SEQ_LISTA) return;

  ERROR_CUENTAS = r.error ? r.error.message : '';
  CUENTAS = r.error ? [] : ((r.data || []) as Cuenta[]);
  LEIDA = !r.error;
  //  Vacía sin error puede ser «no hay ninguno» o «no te deja»: `proveedores`
  //  la lee cualquiera, así que se le pregunta a ella.
  HAY_PROVEEDORES = null;
  if (!r.error && !CUENTAS.length){
    const h = await pedir(sb.from('proveedores').select('id').limit(1));
    if (mio !== SEQ_LISTA) return;
    HAY_PROVEEDORES = h.error ? null : (h.data || []).length > 0;
  }
  if (r.error) avisoHtml('#pvMsg','bad',
    'No se pudieron leer las cuentas de los proveedores (' + esc(r.error.message) + '). ' +
    'Lo de arriba no es la lista: es que no se pudo leer. Se reintenta volviendo a ' +
    'entrar a la pestaña.');
  else limpiar('#pvMsg');

  //  La moneda, en los tres lugares que la nombran; el HTML no trae ninguna: una
  //  moneda que no se leyó no se muestra (D83).
  $('#pvMonedaTit').textContent = rotulo();
  $('#pvHint').innerHTML = CARGO_AUTO === false
    ? 'En esta instalación los remitos <b>no</b> generan deuda sola (está apagado ' +
      '<b>proveedor.cargo_automatico</b>): la deuda es la que se carga a mano con un ajuste, ' +
      'por ejemplo al llegar la factura, y baja con cada pago, que sale de una caja.'
    : CARGO_AUTO === null
    ? 'La deuda baja con cada pago, que sale de una caja. No se pudo leer si en esta ' +
      'instalación los remitos la generan solos.'
    : 'La deuda nace sola con cada remito que trae costo, y baja con cada pago, que sale ' +
      'de una caja.';
  $$('.pvMonedaAj').forEach(e => e.textContent = rotulo());
  if (ERROR_MONEDA) avisoHtml('#pvCfgMsg','warn',
    'No se pudo leer en qué moneda se lleva la cuenta de los proveedores (' +
    esc(ERROR_MONEDA) + '). Los números de abajo son los de la base; lo que no se ' +
    'puede decir es en qué moneda están, ni cuánto es eso en otra.');
  else limpiar('#pvCfgMsg');

  pintarLista();
  if (ABIERTA != null) await abrirCuenta(ABIERTA, true);
}

// ---------------------------------------------------------------------
//  La lista
// ---------------------------------------------------------------------
//  El saldo es positivo cuando la empresa debe, negativo cuando pagó de más
//  (un anticipo: la base no lo frena, D113). Cero es «saldada» — pero sólo si
//  la cuenta sabe todo: con remitos sin costo, lo que se sabe es cero.
const DEUDA = 0.005;
function celdaSaldo(c: Cuenta){
  const s = Number(c.saldo);
  if (s > DEUDA)  return `<b>${num(s)}</b>`;
  //  «A favor» y «saldada» son afirmaciones sobre TODA la cuenta: con
  //  remitos sin costo, lo único que se puede decir es lo que se sabe (D106).
  if (s < -DEUDA) return `<span class="chip c-ok">${num(-s)} a favor</span>` +
    (noSabeTodo(c) ? ' <span class="hint">de lo que se sabe</span>' : '');
  return noSabeTodo(c) ? '<span class="hint">0 de lo que se sabe</span>' : '—';
}
//  Si la cuenta tiene remitos sin costo que TENDRÍAN que haber generado
//  deuda. Con el cargo automático apagado ningún remito la genera, así que
//  no le falta nada por eso.
const noSabeTodo = (c: Cuenta) => CARGO_AUTO !== false && Number(c.recepciones_sin_costo) > 0;

//  La columna de lo que la cuenta NO sabe. `recepciones_sin_costo` es null sólo
//  sin `proveedor.ver_cuenta` (y entonces no hay filas); igual no se afirma un
//  cero por si la vista cambia (D105: un null no es un cero).
function celdaSinCosto(c: Cuenta){
  const n = c.recepciones_sin_costo;
  if (n == null) return '<span class="chip c-warn">no se sabe</span>';
  if (!Number(n) || CARGO_AUTO === false) return '—';
  return `<span class="chip c-warn">${num(Number(n))} remito${Number(n) === 1 ? '' : 's'} con líneas sin costo</span>`;
}

function pintarLista(){
  if (ERROR_CUENTAS){
    $('#tblProveedoresCC').innerHTML =
      '<tr><td colspan="4" class="vacio">No se pudieron leer las cuentas</td></tr>';
    return;
  }
  const q = ($('#pvBusca').value || '').toLowerCase();
  const soloDeuda = $('#pvSoloDeuda').checked;
  const f = CUENTAS.filter(c =>
    (!q || (c.nombre || '').toLowerCase().includes(q)) &&
    (!soloDeuda || Number(c.saldo) > DEUDA));
  $('#tblProveedoresCC').innerHTML = f.map(c => `
    <tr data-pv="${c.proveedor_id}" style="cursor:pointer">
      <td><b>${esc(c.nombre)}</b>${c.activo === false
        ? ' <span class="chip">dado de baja</span>' : ''}</td>
      <td class="num">${celdaSaldo(c)}</td>
      <td>${celdaSinCosto(c)}</td>
      <td class="mono">${c.ultimo_movimiento ? esc(fecha(c.ultimo_movimiento)) : '—'}</td>
    </tr>`).join('') || `<tr><td colspan="4" class="vacio">${
      //  Vacíos distintos: filtro de texto, filtro de deuda, ninguno cargado.
      q ? 'Ningún proveedor coincide con el filtro'
      : soloDeuda ? 'No se le debe nada a ningún proveedor (de lo que la cuenta sabe)'
      : HAY_PROVEEDORES
        ? 'Hay proveedores cargados, pero la base no te deja ver su cuenta: se te sacó ' +
          'el permiso mientras tenías la pestaña abierta. Recargá la página.'
      : HAY_PROVEEDORES === null
        ? 'La base no devolvió ninguna cuenta, y no se pudo saber si es porque no hay ' +
          'proveedores o porque no te deja verlos'
      : 'Todavía no hay proveedores cargados'}</td></tr>`;
  $$('[data-pv]', $('#tblProveedoresCC')).forEach(tr =>
    tr.onclick = () => abrirCuenta(Number(tr.dataset.pv)));
}
$('#pvBusca').oninput = pintarLista;
$('#pvSoloDeuda').onchange = pintarLista;

// ---------------------------------------------------------------------
//  Una cuenta
// ---------------------------------------------------------------------
const NOMBRE_TIPO: Record<string, string> = {
  cargo: 'Cargo', pago: 'Pago', ajuste: 'Ajuste', nota_credito: 'Nota de crédito' };

//  De dónde salió un cargo. Los dos tipos que escribe la base (0050, 0051):
//  el remito entero y la línea cuyo costo se cargó o corrigió después.
function origen(m: any){
  if (m.referencia_tipo === 'recepcion')
    return ` <span class="chip">recepción #${esc(m.referencia_id)}</span>`;
  //  La misma referencia la escriben DOS funciones: `cargar_costo` con un
  //  cargo y `corregir_costo` con un ajuste (0051). El chip dice cuál.
  if (m.referencia_tipo === 'recepcion_item')
    return m.tipo === 'ajuste'
      ? ` <span class="chip">corrección del costo · línea #${esc(m.referencia_id)}</span>`
      : ` <span class="chip">costo cargado después · línea #${esc(m.referencia_id)}</span>`;
  return '';
}

//  Desde la ficha del proveedor (D223): a su cuenta, en esta pestaña.
export async function irACuentaDe(id: number){
  $('#tabProveedores').click();
  await cargarProveedores();
  await abrirCuenta(id);
}

async function abrirCuenta(id: number, silencioso = false){
  const cambio = ABIERTA !== id;
  ABIERTA = id;
  const mio = ++SEQ_CUENTA;
  if (cambio){
    ANULANDO = null;
    $('#pvAnularBox')?.remove();
    //  Los formularios se esconden mientras llega la cuenta nueva: con los de la
    //  anterior a la vista, «Registrar el pago» le pagaba al proveedor
    //  RECIÉN tocado el monto sugerido para el otro.
    ocultarForms();
    //  Y el ajuste escrito para el otro no viaja a éste.
    $('#pvaMonto').value = ''; $('#pvaMotivo').value = ''; $('#pvaBaja').checked = true;
  }
  //  La tarjeta se destapa ANTES de cualquier `return`: sus carteles viven adentro.
  //  La cuenta ocupa la pestaña, con «← Volver a los proveedores» (D231).
  abrirComoFicha($('#cardPvCuenta'), { lista: 'los proveedores', nombre: 'la cuenta',
    alCerrar: limpiarCuenta });
  if (!silencioso){ limpiar('#pvMovsMsg'); limpiar('#pvpMsg'); limpiar('#pvaMsg'); }

  const c = CUENTAS.find(x => Number(x.proveedor_id) === id);
  if (!c){
    $('#pvTit').textContent = 'Cuenta';
    limpiar('#pvSinCostoMsg');
    $('#tblPvMovs').innerHTML =
      '<tr><td colspan="7" class="vacio">No se pudo leer esta cuenta</td></tr>';
    aviso('#pvMovsMsg','bad', ERROR_CUENTAS
      ? 'Esta cuenta no está en la lista porque las cuentas no se pudieron leer (' +
        ERROR_CUENTAS + ').'
      : 'Esa cuenta ya no está en la lista.');
    MOVS = []; ANULANDO = null;
    $('#pvAnularBox')?.remove();
    ocultarForms();
    return;
  }

  const s = Number(c.saldo);
  const deLoQueSeSabe = noSabeTodo(c) ? ' de lo que se sabe' : '';
  $('#pvTit').innerHTML = `Cuenta de ${esc(c.nombre)} — ` +
    (s > DEUDA ? `se le debe <b>${num(s)} ${esc(rotulo())}</b>`
     : s < -DEUDA ? `tiene <b>${num(-s)} ${esc(rotulo())}</b> a favor${deLoQueSeSabe}`
     : noSabeTodo(c) ? 'en cero de lo que se sabe' : 'saldada') +
    (c.activo === false ? ' <span class="chip">dado de baja</span>' : '');
  //  Lo que la cuenta no sabe, arriba de los números (D106). Sólo a quien tiene
  //  `costo.cargar` se le manda dónde cargarlos.
  const n = Number(c.recepciones_sin_costo || 0);
  if (n > 0 && noSabeTodo(c)) avisoHtml('#pvSinCostoMsg','warn',
    `<b>${num(n)} remito${n === 1 ? '' : 's'}</b> de este proveedor ${n === 1 ? 'tiene' : 'tienen'} ` +
    'líneas que entraron <b>sin costo</b>. Esas líneas no generaron deuda, así que lo que ' +
    'se le debe puede ser <b>más</b> de lo que dice acá.' +
    (puede('costo','cargar') && puede('recepcion','ver')
      ? ' El costo que falta se carga en <b>Recepción → Costos de lo recibido</b>, y ahí la deuda se suma sola.'
      : ''));
  else limpiar('#pvSinCostoMsg');

  //  El estado cero de los movimientos: se vacía antes de preguntar, o los
  //  botones «Anular» de OTRO proveedor apuntarían a pagos ajenos.
  if (cambio){
    MOVS = [];
    $('#tblPvMovs').innerHTML =
      '<tr><td colspan="7" class="vacio">Leyendo los movimientos…</td></tr>';
  }

  const { data, error } = await pedir(sb.from('v_proveedor_movimientos')
    .select('id,tipo,monto,monto_origen,cotizacion,moneda,medio_pago,caja,caja_id,referencia_tipo,referencia_id,motivo,usuario,ocurrido_en,revierte_a,anulado')
    .eq('proveedor_id', id).order('id', { ascending: false }).limit(TOPE_MOVS));
  if (mio !== SEQ_CUENTA) return;
  ERROR_MOVS = error ? error.message : '';
  MOVS = error ? [] : (data || []);
  if (error) avisoHtml('#pvMovsMsg','bad',
    'No se pudieron leer los movimientos de esta cuenta (' + esc(error.message) + '). ' +
    'Lo de arriba no es lo que se movió: es que no se pudo leer. Se reintenta ' +
    'volviendo a tocar el proveedor.');
  //  La relectura silenciosa (después de cada acto) borra el cartel de una
  //  lectura caída anterior, pero no el resultado del acto que la disparó.
  else if ($('#pvMovsMsg .a-bad')) limpiar('#pvMovsMsg');

  pintarMovs(c);
  pintarPago(c);
  pintarAjuste();
}

function pintarMovs(c: Cuenta){
  if (ERROR_MOVS){
    $('#tblPvMovs').innerHTML =
      '<tr><td colspan="7" class="vacio">No se pudieron leer los movimientos</td></tr>';
    return;
  }
  const veCaja = puede('caja','ver');
  const puedeAnular = puede('proveedor','pagar');
  $('#tblPvMovs').innerHTML = MOVS.map(m => {
    const monto = Number(m.monto);
    const esReversa = m.revierte_a != null;
    //  El nombre de la caja lo tapa `caja.ver` (el dueño no lo tiene, D115). El
    //  id llega siempre (0052): el nombre se busca en `mis_cajas()`, y si no
    //  está se dice que no se puede ver, nunca un hueco («ninguna caja»).
    const caja = m.caja ?? nombreCaja(m.caja_id);
    //  Un pago SALIÓ de la caja, su anulación VOLVIÓ a ella.
    const flecha = esReversa ? 'volvió a' : 'salió de';
    const dondeCaja = m.caja_id == null ? ''
      : caja ? ` <span class="hint">${flecha} ${esc(caja)}</span>`
      : veCaja ? ''
      //  Sin el nombre hay TRES causas: la lista de cajas no se pudo leer; esa
      //  caja no está (`mis_cajas` trae sólo las activas); o no hay permiso de caja.
      : E_MIS_CAJAS
        ? ' <span class="chip c-warn">no se pudo leer la lista de cajas: no se sabe cuál</span>'
      : muevePlata()
        ? ' <span class="chip c-warn">de una caja que hoy está desactivada</span>'
      : ' <span class="chip c-warn">no podés ver de qué caja</span>';
    return `<tr${m.anulado || esReversa ? ' style="opacity:.6"' : ''}>
      <td class="mono">${esc(fecha(m.ocurrido_en))}</td>
      <td>${esc(NOMBRE_TIPO[m.tipo] || m.tipo)}${
          m.anulado ? ' <span class="chip c-bad">anulado</span>' : ''}${
          esReversa ? ' <span class="chip">anulación</span>' : ''}</td>
      <td class="num"><b>${monto > 0 ? '+' : ''}${num(monto)}</b></td>
      <td>${esc(m.motivo || '—')}${origen(m)}</td>
      <td>${m.moneda && m.medio_pago
          ? `${esc(m.medio_pago)} ${esc(m.moneda)} ${
              //  `num()` convierte el null en cero: diría que se pagó cero (regla 14).
              m.monto_origen == null ? '(no se sabe cuánto)' : num(Math.abs(Number(m.monto_origen)))}` +
            dondeCaja
          : '—'}</td>
      <td>${esc(m.usuario || '—')}</td>
      <td>${botonAnular(m, puedeAnular)}</td></tr>`;
  }).join('') || '<tr><td colspan="7" class="vacio">Esta cuenta no tiene movimientos</td></tr>';
  //  Con el tope lleno puede haber más: se dice, abajo de la tabla (D122).
  if (MOVS.length >= TOPE_MOVS)
    $('#tblPvMovs').insertAdjacentHTML('beforeend',
      `<tr><td colspan="7" class="hint">Se muestran los ${num(TOPE_MOVS)} movimientos más nuevos: ` +
      'puede haber más. El saldo de arriba es el de todos.</td></tr>');

  $$('[data-pvanular]', $('#tblPvMovs')).forEach(b =>
    b.onclick = () => { ANULANDO = Number(b.dataset.pvanular); pintarMovs(c); pintarConfirmar(c); });
  pintarConfirmar(c);
}

//  Se anula un PAGO no anulado ni anulación, con `proveedor.pagar` y habilitado
//  en la caja de donde salió (0031, D93). `false` no se ofrece y se dice por
//  qué; `null` (no se sabe) se ofrece y contesta la base.
function botonAnular(m: any, puedeAnular: boolean){
  if (m.tipo !== 'pago' || m.anulado || m.revierte_a != null || !puedeAnular) return '';
  if (ANULACION_INCIERTA.has(Number(m.id)))
    return '<span class="hint">No se sabe si quedó anulado: volvé a entrar a la pestaña.</span>';
  if (puedoOperar(m.caja_id) === false)
    return `<span class="hint">${noHabilitado(m.caja ?? nombreCaja(m.caja_id), 'anular un pago de')}</span>`;
  return `<button class="b b-sec" data-pvanular="${m.id}"${
    ANULANDO === Number(m.id) ? ' disabled' : ''}>Anular</button>`;
}

//  El cuadro de anular, debajo de la tabla y no en un `prompt()`: el motivo es
//  obligatorio y, si la base rechaza, el error aparece sin borrar lo escrito.
function pintarConfirmar(c: Cuenta){
  const m = MOVS.find(x => Number(x.id) === ANULANDO);
  const viejo = document.getElementById('pvAnularBox');
  if (!m || m.anulado){ ANULANDO = null; viejo?.remove(); return; }
  //  El cuadro es de UN pago: abierto para ése no se toca (motivo a medio
  //  escribir); si es de otro, se reemplaza.
  if (viejo && viejo.dataset.mov === String(m.id)) return;
  viejo?.remove();
  const box = document.createElement('div');
  box.id = 'pvAnularBox';
  box.dataset.mov = String(m.id);
  box.className = 'confirmar grave';
  box.innerHTML = `
    <div class="conf-tit">Anular el pago de ${esc(m.moneda)} ${num(Math.abs(Number(m.monto_origen)))} a ${esc(c.nombre)}</div>
    <p class="conf-txt">La deuda vuelve a subir en <b>${num(Math.abs(Number(m.monto)))} ${esc(rotulo())}</b> y la
      plata vuelve a la caja de donde salió. Queda escrito con tu nombre y el motivo.</p>
    <label>Por qué <input id="pvAnMotivo" placeholder="Se cargó dos veces"></label>
    <div class="fila-acc" style="margin-top:0.471rem">
      <button class="b b-sec" id="pvAnNo" type="button">Dejarlo como está</button>
      <button class="b b-peligro" id="pvAnOk" type="button">Anular el pago</button>
    </div>
    <div id="pvAnMsg"></div>`;
  document.getElementById('pvMovsMsg')!.before(box);
  $('#pvAnNo').onclick = () => { ANULANDO = null; box.remove(); pintarMovs(c); };
  $('#pvAnOk').onclick = () => anularPago(m.id);
}

async function anularPago(id: number){
  const motivo = ($('#pvAnMotivo').value || '').trim();
  if (!motivo){
    aviso('#pvAnMsg','bad','Escribí por qué: anular un pago es plata, y la base no lo deja sin motivo.');
    $('#pvAnMotivo').focus();
    return;
  }
  const cuenta = ABIERTA;
  //  Los DOS botones del cuadro se apagan mientras vuela: cerrarlo en viaje
  //  perdería el cartel de «no contestó» y dejaría volver a pedirla.
  const btn = $('#pvAnOk'), no = $('#pvAnNo');
  btn.disabled = true; no.disabled = true;
  const { data, error } = await rpc('anular_pago_proveedor',
    { p_movimiento_id: id, p_motivo: motivo });
  const box = document.getElementById('pvAnularBox');
  if (error){
    //  Si no contestó, quizá quedó anulado (D91): no se vuelve a ofrecer hasta
    //  volver a entrar.
    if (noContesto(error)){
      ANULACION_INCIERTA.add(id);
      ANULANDO = null;
      box?.remove();
      if (ABIERTA === cuenta){
        const c = cuentaAbierta();
        if (c) pintarMovs(c);
        aviso('#pvMovsMsg','bad', error.message);
      } else aviso('#pvMsg','bad', error.message);
      return;
    }
    btn.disabled = false; no.disabled = false;
    if (box) aviso('#pvAnMsg','bad', error.message);
    return;
  }
  ANULANDO = null;
  box?.remove();
  await cargarProveedores(false);
  //  El cartel va en la cuenta del pago; si mientras tanto se abrió otra, arriba.
  aviso(ABIERTA === cuenta ? '#pvMovsMsg' : '#pvMsg','ok', data?.mensaje ?? 'El pago quedó anulado.');
}

// ---------------------------------------------------------------------
//  El pago
// ---------------------------------------------------------------------
function ocultarForms(){
  $('#formPvPago').classList.add('oculto');
  $('#formPvAjuste').classList.add('oculto');
  limpiar('#pvPagoNoMsg');
}

//  Sin los medios o las monedas no se puede pagar (el pago guarda medio y
//  moneda). La moneda de la cuenta NO frena el pago (convierte la base): sólo
//  apaga el equivalente.
function frenoPago(){
  const monedas = frenoMonedas();
  if (monedas) return monedas;
  if (ERROR_MEDIOS)
    return 'No se pudieron leer los medios de pago (' + esc(ERROR_MEDIOS) + '). ' +
      '<b>No es que no haya: es que no se pudieron leer.</b> Cambiá de pestaña y volvé.';
  //  Vacíos de verdad: no son falla pero no dejan pagar (un `<select>` sin
  //  opciones manda `Number('')`, un cero).
  if (!MEDIOS.length) return 'No hay ningún medio de pago activo, así que no se puede registrar un pago.';
  if (!MONEDAS.length) return 'No hay ninguna moneda activa, así que no se puede registrar un pago.';
  return '';
}

function pintarPago(c: Cuenta){
  limpiar('#pvPagoNoMsg');
  if (!puede('proveedor','pagar')){ $('#formPvPago').classList.add('oculto'); return; }
  //  La configuración apunta a una caja que no está (`mis_cajas` se leyó, la
  //  fila del acto vino, sin caja): se SABE que la base rechaza; no se ofrece y
  //  se dice qué revisar.
  const fila = MIS_CAJAS.find(x => x.acto === 'proveedor');
  if (!E_MIS_CAJAS && fila && fila.caja_id == null){
    $('#formPvPago').classList.add('oculto');
    avisoHtml('#pvPagoNoMsg','bad',
      `La configuración dice que los pagos a proveedores salen de la caja <b>${esc(fila.codigo)}</b>, ` +
      'y esa caja no existe o está desactivada, así que la base va a rechazar cualquier pago. ' +
      'Hay que revisar <b>proveedor.caja</b>.');
    return;
  }
  //  La caja del pago la dice la configuración; si se puede operar, la base (D93).
  //  Sólo un `false` cierra.
  if (puedoActo('proveedor') === false){
    $('#formPvPago').classList.add('oculto');
    avisoHtml('#pvPagoNoMsg','warn', noHabilitado(cajaDelActo('proveedor'), 'pagarle a un proveedor desde') +
      ' El pago a un proveedor sale siempre de esa caja, así que lo tiene que registrar ' +
      'alguien habilitado en ella.');
    return;
  }
  $('#formPvPago').classList.remove('oculto');
  const caja = cajaDelActo('proveedor');
  $('#pvpCaja').innerHTML = caja
    ? `Sale de la <b>${esc(caja)}</b>, que es la caja de los pagos a proveedores.`
    : 'Sale de la caja que la configuración dice para los pagos a proveedores ' +
      '(no se pudo leer la lista de cajas, así que no se sabe cuál es ni si la podés ' +
      'operar: si no, la base lo va a rechazar).';

  const freno = frenoPago();
  //  Con `repintar()` (regla 12): no perder el medio y la moneda elegidos al releer.
  repintar('#pvpMedio', ERROR_MEDIOS
    ? '<option value="">no se pudieron leer los medios de pago</option>'
    : !MEDIOS.length ? '<option value="">no hay medios de pago activos</option>'
    : MEDIOS.map((m: any) => `<option value="${m.id}">${esc(m.nombre)}</option>`).join(''));
  repintar('#pvpMoneda', frenoMonedas()
    ? '<option value="">no se pudieron leer las monedas</option>'
    : MONEDAS.map((m: any) => `<option value="${m.id}" data-cod="${esc(m.codigo)}"${
        m.codigo === MONEDA ? ' selected' : ''}>${esc(m.codigo)}</option>`).join(''));
  if (freno)
    avisoHtml('#pvpCatMsg','bad', freno + '<br>Mientras tanto no se puede registrar un pago.');
  else limpiar('#pvpCatMsg');
  //  Lo escrito se respeta en las relecturas: se vuelve a sugerir sólo con una
  //  cuenta nueva o después de un pago.
  if (PAGO_DE !== c.proveedor_id){
    PAGO_DE = c.proveedor_id;
    $('#pvpMotivo').value = '';
    prellenar(c);
  } else equivalente(c);
}

const codPago = () => $('#pvpMoneda').selectedOptions[0]?.dataset.cod || null;

//  Cuánto vale `monto` de la moneda `desde` en la moneda `hacia`, con las
//  cotizaciones vigentes (la base vale 1). `null` si falta alguna.
function convertir(monto: number, desde: string | null, hacia: string | null){
  if (!desde || !hacia || !Number.isFinite(monto)) return null;
  const a = (COTIZ as any)[desde], b = (COTIZ as any)[hacia];
  if (!a || !b) return null;
  return Math.round(monto * a / b * 100) / 100;
}

//  El monto arranca con lo que se debe, pasado a la moneda elegida. Sin
//  deuda arranca vacío: un anticipo se puede, pero no se sugiere.
function prellenar(c: Cuenta){
  const deuda = Number(c.saldo);
  const v = deuda > DEUDA && !frenoPago() ? convertir(deuda, MONEDA, codPago()) : null;
  //  Hacia abajo, al centavo: sugerir un centavo de más deja la cuenta a
  //  favor por redondeo.
  $('#pvpMonto').value = v === null ? '' : String(Math.floor(v * 100 + 1e-6) / 100);
  equivalente(c);
}

function equivalente(c: Cuenta){
  const cod = codPago();
  const monto = Number($('#pvpMonto').value);
  const freno = frenoPago();
  //  La puerta chica de la cotización: en la moneda de la cuenta no hace falta
  //  ninguna; en otra, sin cotización la base rechaza. No poder LEERLAS no es lo
  //  mismo que que no haya (regla del 13/9). La base convierte con DOS
  //  cotizaciones (`app.a_costo`; la base vale 1): si falta la de la cuenta,
  //  NINGÚN pago pasa; si falta sólo la otra, en la moneda de la cuenta sí. Cada
  //  cartel nombra la que falta de verdad.
  const sinCot = (m: string | null) => !!m && !(COTIZ as any)[m];
  const faltaCuenta = !freno && !ERROR_COTIZ_COBRO && sinCot(MONEDA);
  const falta = !freno && !ERROR_COTIZ_COBRO && !faltaCuenta && !!MONEDA && sinCot(cod);
  $('#pvpOk').disabled = !!freno || faltaCuenta || trabado('#pvpOk') || PAGANDO;
  if (faltaCuenta) avisoHtml('#pvpCotMsg','bad',
    `No hay cotización de ${esc(MONEDA)} cargada, que es la moneda de la cuenta: la base ` +
    'va a rechazar cualquier pago hasta que se cargue.');
  else if (falta) avisoHtml('#pvpCotMsg','bad',
    `No hay cotización de ${esc(cod)} cargada: la base va a rechazar el pago. En ${esc(MONEDA)} se puede pagar igual.`);
  else limpiar('#pvpCotMsg');
  const eq = convertir(monto, cod, MONEDA);
  const deuda = Number(c.saldo);
  $('#pvpEquiv').innerHTML = (!monto || freno || falta || faltaCuenta) ? ''
    : !MONEDA ? '<span class="chip c-warn">No se sabe en qué moneda se lleva la cuenta, así que no se puede mostrar a cuánto equivale.</span>'
    : eq === null
      ? (ERROR_COTIZ_COBRO
          ? `<span class="chip c-warn">No se pudieron leer las cotizaciones: no se puede mostrar a cuánto equivale. El pago en ${esc(cod)} lo convierte la base.</span>`
          : '')
    : `Baja la deuda en <b>${num(eq)} ${esc(MONEDA)}</b>` +
      (eq > deuda + DEUDA
        ? ` — <span class="chip c-warn">es más de lo que se le debe${noSabeTodo(c) ? ' (de lo que se sabe)' : ''}: queda ${num(Math.round((eq - deuda) * 100) / 100)} a favor</span>` : '');
}

$('#pvpMoneda').onchange = () => { const c = cuentaAbierta(); if (c) prellenar(c); };
$('#pvpMonto').oninput = () => { const c = cuentaAbierta(); if (c) equivalente(c); };
const cuentaAbierta = () => CUENTAS.find(x => Number(x.proveedor_id) === ABIERTA) || null;

$('#pvpOk').onclick = async () => {
  limpiar('#pvpMsg');
  const c = cuentaAbierta();
  if (!c){ aviso('#pvpMsg','bad','Esa cuenta ya no está en la lista.'); return; }
  const freno = frenoPago();
  if (freno){ avisoHtml('#pvpMsg','bad', freno); return; }
  const monto = Number($('#pvpMonto').value);
  if (!(monto > 0)){ aviso('#pvpMsg','bad','Poné cuánto le estás pagando.'); return; }
  const btn = $('#pvpOk');
  const cuenta = c.proveedor_id;
  //  EN VIAJE: ningún repintado lo enciende hasta que termine todo, relectura
  //  incluida (si no, pago doble).
  PAGANDO = true;
  btn.disabled = true;
  const { data, error } = await rpc('pagar_proveedor', {
    p_proveedor_id : c.proveedor_id,
    p_monto        : monto,
    p_moneda_id    : Number($('#pvpMoneda').value),
    p_medio_pago_id: Number($('#pvpMedio').value),
    p_motivo       : $('#pvpMotivo').value.trim() || null,
  });
  //  Sin respuesta, el pago quizá quedó registrado: el botón no vuelve hasta
  //  volver a entrar a la pestaña (D91, D92).
  trabarSiNoContesto('#pvpOk', error);
  if (error){
    PAGANDO = false;
    if (!trabado('#pvpOk')) btn.disabled = false;
    aviso('#pvpMsg','bad', error.message);
    return;
  }
  //  Hecho: el monto se borra ANTES de releer; la sugerencia vuelve con el saldo nuevo.
  $('#pvpMonto').value = '';
  if (PAGO_DE === cuenta) PAGO_DE = null;
  try { await cargarProveedores(false); }
  finally {
    PAGANDO = false;
    const c2 = cuentaAbierta();
    if (c2) equivalente(c2);
  }
  //  Con `?.`: un TypeError acá, DESPUÉS de pagar, dejaría la pantalla como si
  //  no hubiera pasado nada. El cartel va en la cuenta del pago, o arriba.
  aviso(ABIERTA === cuenta ? '#pvpMsg' : '#pvMsg','ok', data?.mensaje ?? 'El pago quedó registrado.');
};

// ---------------------------------------------------------------------
//  El ajuste
// ---------------------------------------------------------------------
function pintarAjuste(){
  $('#formPvAjuste').classList.toggle('oculto', !puede('proveedor','ajustar'));
  //  El botón lo enciende la carga de la pestaña, el camino que manda el cartel
  //  de «no contestó» (si no, quedaría apagado para siempre).
  $('#pvaOk').disabled = trabado('#pvaOk') || AJUSTANDO;
}

$('#pvaOk').onclick = async () => {
  limpiar('#pvaMsg');
  const c = cuentaAbierta();
  if (!c){ aviso('#pvaMsg','bad','Esa cuenta ya no está en la lista.'); return; }
  const monto = Number($('#pvaMonto').value);
  if (!(monto > 0)){ aviso('#pvaMsg','bad','Poné de cuánto es el ajuste.'); return; }
  const motivo = ($('#pvaMotivo').value || '').trim();
  if (!motivo){
    aviso('#pvaMsg','bad','Escribí de qué es el ajuste: sin motivo no sirve para nada, y la base no lo deja.');
    return;
  }
  //  El signo lo pone la pantalla y no la persona: «baja la deuda» es un monto
  //  negativo, y un menos a mano es fácil de errar.
  const conSigno = $('#pvaSube').checked ? monto : -monto;
  const btn = $('#pvaOk');
  const cuenta = c.proveedor_id;
  AJUSTANDO = true;
  btn.disabled = true;
  const { data, error } = await rpc('ajustar_proveedor', {
    p_proveedor_id: c.proveedor_id, p_monto: conSigno, p_motivo: motivo });
  AJUSTANDO = false;
  trabarSiNoContesto('#pvaOk', error);
  if (!trabado('#pvaOk')) btn.disabled = false;
  if (error){ aviso(ABIERTA === cuenta ? '#pvaMsg' : '#pvMsg','bad', error.message); return; }
  //  Lo escrito se borra sólo si sigue siendo de ESA cuenta.
  if (ABIERTA === cuenta){ $('#pvaMonto').value = ''; $('#pvaMotivo').value = ''; }
  await cargarProveedores(false);
  aviso(ABIERTA === cuenta ? '#pvaMsg' : '#pvMsg','ok', data?.mensaje ?? 'El ajuste quedó registrado.');
};

function limpiarCuenta(){
  ABIERTA = null; ANULANDO = null; PAGO_DE = null;
  document.getElementById('pvAnularBox')?.remove();
  //  Y cualquier respuesta en viaje de esa cuenta deja de valer.
  SEQ_CUENTA++;
}
$('#pvCerrar').onclick = () => cerrarComoFicha($('#cardPvCuenta'));
